import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ChatSession } from '@/api/types/chat.type'

interface Message {
  id: string
  content: string
  isUser: boolean
  timestamp: Date
  avatar: string
}

interface ChatStore {
  sessions: ChatSession[]
  messages: Map<string, Message[]>
  addSession: (session: ChatSession) => void
  removeSession: (sessionId: string) => void
  updateSession: (sessionId: string, title: string) => void
  clearSessions: () => void
  addMessage: (sessionId: string, message: Message) => void
  getMessages: (sessionId: string) => Message[]
  clearMessages: (sessionId: string) => void
}

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      sessions: [],
      messages: new Map(),

      addSession: (session) =>
        set((state) => ({
          sessions: [session, ...state.sessions],
        })),

      removeSession: (sessionId) =>
        set((state) => {
          const newMessages = new Map(state.messages)
          newMessages.delete(sessionId)
          return {
            sessions: state.sessions.filter(
              (session) => session.sessionId !== sessionId
            ),
            messages: newMessages,
          }
        }),

      updateSession: (sessionId, title) =>
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.sessionId === sessionId
              ? { ...session, title, updatedAt: new Date().toISOString() }
              : session
          ),
        })),

      clearSessions: () => set({ sessions: [], messages: new Map() }),

      addMessage: (sessionId, message) =>
        set((state) => {
          const newMessages = new Map(state.messages)
          const sessionMessages = newMessages.get(sessionId) || []

          // 检查消息是否已存在，避免重复添加
          const messageExists = sessionMessages.some(
            (existingMessage) => existingMessage.id === message.id
          )

          if (!messageExists) {
            newMessages.set(sessionId, [...sessionMessages, message])
            return { messages: newMessages }
          }

          // 如果消息已存在，返回原状态
          return state
        }),

      getMessages: (sessionId) => {
        const state = get()
        return state.messages.get(sessionId) || []
      },

      clearMessages: (sessionId) =>
        set((state) => {
          const newMessages = new Map(state.messages)
          newMessages.delete(sessionId)
          return { messages: newMessages }
        }),
    }),
    {
      name: 'chat-store',
      partialize: (state) => ({
        sessions: state.sessions,
        messages: Array.from(state.messages.entries()),
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.messages = new Map(state.messages as any)
        }
      },
    }
  )
)
